import { CharterPaymentState, CharterStatus } from 'Modules/Charter/Types/CharterScheduleItem';

/**
 * A charter as its customer reads it.
 *
 * A shape of its own rather than `CharterScheduleItem`: the customer is shown less of a charter than
 * staff are (no notes and no payment method), and the two would drift apart the moment either side
 * gained a field.
 *
 * Matches the server's `CustomerCharterDto` field for field. `CustomerCharterApi` is the only thing
 * that turns one into the other, and the two date-times are the sole conversion.
 */
export interface CustomerCharter {
	id: string;

	/**
	 * The charter's reference, which is what the customer quotes when they ring up about one that has
	 * not been invoiced. Absent only on a charter booked before charters had one.
	 */
	humanReadableId?: string;

	/** Absent on a charter booked before anyone named it, which reads as "Charter". */
	name?: string;

	/** The ferry hired, by name. */
	ferryName: string;

	/** The block the ferry is held for, as the operator agreed it. */
	startDateTime: Date;
	endDateTime: Date;

	/** The hire price for the whole ferry, never a price per person. */
	price?: number;

	/** What the customer quotes when they ring up. */
	invoiceNumber?: string;

	/**
	 * Where the money is up to, in the same three words staff read — a charter for an invoiced account
	 * reads `Awaiting` to the customer too, the way their booking already does.
	 *
	 * Absent on a charter booked before charter payment existed, which has no transaction to read and
	 * shows as unpaid.
	 */
	paymentState?: CharterPaymentState;

	/**
	 * Whether the charter has been handed to accounting, shown in place of awaiting — as
	 * `Booking.isInvoiced` is, which outranks awaiting and unpaid alike.
	 */
	isInvoiced?: boolean;

	/**
	 * Booked, or called off. Never `Reserved` — a reserve has no customer view at all, and a cancelled
	 * one answers 404 rather than arriving here.
	 *
	 * Outranks the money in what the page says: a charter that is off is off, whether or not it was
	 * ever paid for.
	 */
	status: Extract<CharterStatus, 'Booked' | 'Cancelled'>;

	/**
	 * When the charter was first written down, whatever state it was written down in — a charter
	 * pencilled in as a reserve and booked later still carries the day it was first written down.
	 *
	 * A real instant, not the wall-clock the held block is: it is read in the reader's own zone.
	 */
	created: Date;

	/**
	 * Answers to the dynamic fields aimed at charters, as pairs of field id and answer. The labels
	 * come from the dynamic booking field store, so a renamed field reads the same here as on a
	 * booking.
	 */
	dynamicFields?: Array<{ fieldId: string; value: string }>;
}

/** What the charter is called wherever one is shown, including one nobody named. */
export function customerCharterName(charter: CustomerCharter): string {
	return charter.name?.trim() || 'Charter';
}

/**
 * Whether the charter is over.
 *
 * Read off the end rather than the start, so a charter that is out on the water right now is still
 * upcoming — the customer has not finished with it.
 *
 * Takes the charter and nothing else, so it can be handed straight to `filter` — a second parameter
 * would be filled with the array index there, which is not a date.
 */
export function customerCharterHasEnded(charter: CustomerCharter): boolean {
	return charter.endDateTime.getTime() < Date.now();
}

/**
 * Whether the charter belongs under **Past** rather than **Upcoming**.
 *
 * A cancelled one always does, whatever its dates say: the ferry is not being held any more, so a
 * charter that was called off next Tuesday is not something the customer has coming up. Reading it
 * off the dates alone put cancelled charters back among the ones that are going ahead.
 */
export function customerCharterIsPast(charter: CustomerCharter): boolean {
	return charter.status === 'Cancelled' || customerCharterHasEnded(charter);
}
